import { handleMobileDesktopSubmenuClicks, initializeNavDropdown, StickyHeader } from "../../global";
import apiRequest from "../api/apiRequest";
import { cachedAPIWrapper, getCachedAPIResponse } from "../apiCaching/apiWrapper";
import { apiConfig } from "../config/apiConfig";
import { ScreenType } from "../location-search-map/models/ScreenType";
import { reportScreenSize } from "../location-search-map/reportScreenSize";
import { updateHeaderLogo } from "../util/headerUpdate";
import { replaceLegalFooter, replaceNeighborlyFooter } from "../util/localizationService";
import { fetchCrownpeakUrlDetails, getFooterLink, getWeblocationPathGroupWithFooterConfig, modifyAllApplyLocalHref, getBrandDetailsFromBrandJson } from "../util/share";
import { waitForBrandDataBeforeFooterConfig } from "../util/brandLoadingManager";
import { SetLocalHeader } from "./SetLocalHeader";
import {setServices} from "./serviceLinks";
import { isValidTeamJson, TeamJson } from "./meet-the-team-helper";
import { apiCaching } from "../config/cachingConfig";
import { getOfferTeaserPath } from "../util/coupons-event-handling";
import { conceptIdForAllBrands } from "../nbly-brand/helper_functions";

export default class MenuGeneratorDynamic {
  private static instance: MenuGeneratorDynamic;
  private initialized: boolean = false;

  /**
   * Static factory method to create and initialize the singleton instance
   * @returns Promise<MenuGeneratorDynamic> Fully initialized instance
   */
  static async create(): Promise<MenuGeneratorDynamic> {
    if (!MenuGeneratorDynamic.instance) {
      MenuGeneratorDynamic.instance = new MenuGeneratorDynamic();
      await MenuGeneratorDynamic.instance.initialize();
    }
    return MenuGeneratorDynamic.instance;
  }

  /**
   * Get the singleton instance (synchronous access)
   * Note: Use create() for initial setup to ensure async operations complete
   * @returns MenuGeneratorDynamic instance
   */
  static getInstance(): MenuGeneratorDynamic {
    if (!MenuGeneratorDynamic.instance) {
      MenuGeneratorDynamic.instance = new MenuGeneratorDynamic();
    }
    return MenuGeneratorDynamic.instance;
  }

  /**
   * Private constructor - use create() static method for initialization
   */
  private constructor() {}

  /**
   * Async initialization method called by create()
   * Handles all asynchronous operations that were previously in the constructor
   */
  private async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // Retrieve the necessary data once during initialization
    const dbaName: string | null = localStorage.getItem('dbaName');
    const webId: string | null = localStorage.getItem('weblocationId');
    let footerMenuCall = false;

    if (webId && dbaName && !window.location.href.includes(dbaName)) {
      await this.getMenu();
      footerMenuCall = true;
    }
    
    if (!footerMenuCall) {
      await this.getFooterMenu(dbaName);
    }

    const weblocation = (document.getElementById("weblocationId") as HTMLInputElement)?.value ?? 
                        (document.getElementById('local_weblocationId') as HTMLInputElement)?.value ?? 
                        localStorage.getItem('franchiseWebLocationId');
    
    if (weblocation) {
      await this.hideOurTeamsMenu();
      await this.hideOffersMenu();
    }
    
    await this.fetchPhotoGalleryData();
    
    this.initialized = true;
  }

   async hideOurTeamsMenu() {
    try {
      sessionStorage.removeItem("ourTeamObject");
      const localWebID = localStorage.getItem("franchiseWebLocationId");
      const webID = document.getElementById('weblocationId') as HTMLInputElement;
      const localWeblocId = document.getElementById('local_weblocationId') as HTMLInputElement;
      let webLocationId;
      if (webID?.value) {
          webLocationId = webID.value;
      } else if (localWeblocId?.value) {
          webLocationId = localWeblocId.value;
      } else {
          webLocationId = localWebID;
      }
      const apiUrl = apiConfig.ourTeam + webLocationId;
      const result:TeamJson = await cachedAPIWrapper(apiUrl);
      if(result){
      sessionStorage.setItem('ourTeamObject', JSON.stringify(result));
      if (isValidTeamJson(result)) {
        this.hideTeamAndPhotoMenu("team", true);
      }
      else{
        this.hideTeamAndPhotoMenu("team", false);
      }
     }
    } catch (err) {
      console.log('Error in rendering our teams section.', err);
      this.hideTeamAndPhotoMenu("team", false);
    }
  }

  async hideOffersMenu() {
    const conceptCode = (document.getElementById('conceptCode') as HTMLInputElement)?.value;
    const brandData = await getBrandDetailsFromBrandJson(conceptCode);
    const targetItem = document.getElementById("specialoffer_menu");
    if (brandData?.display_special_offers_from_CES && targetItem) {
      const url = await getOfferTeaserPath();
      try {
        if (url) {
          let result = await getCachedAPIResponse(url, apiCaching.OfferTeaser, false);
          if (result?.SpecialOfferMarkup?.trim()) {
            // Show the section
            this.hideTeamAndPhotoMenu("offer", true);
          } else {
            // Hide the section
            this.hideTeamAndPhotoMenu("offer", false);
          }
          
        }
        else {
          this.hideTeamAndPhotoMenu("offer", false);
        }
      }
      catch {
        this.hideTeamAndPhotoMenu("offer", false);
      }
    }
  }
  private hideTeamAndPhotoMenu(menuToBeHidden: string, toShow = false): void {

    let targetItem;
    //We can avoid this entire switch case, but it is used so that we can change the ID authored from CP in one place.
    switch (menuToBeHidden) {
      case 'photo':
        targetItem = document.getElementById("photogallery_menu");
        if (targetItem)
          findParent(targetItem, toShow);
        break;
      case 'team':
        targetItem = document.getElementById("meettheteam_menu");
        if (targetItem)
          findParent(targetItem, toShow);
        break;
      case 'offer':
        targetItem = document.getElementById("specialoffer_menu");
        if (targetItem)
          findParent(targetItem, toShow);
        break;
      default:
        break;
    }

    function findParent(target:any, toShow = false){
      //This is the root menu class defined.
      const menuParentClass = document.querySelector(".main-nav-warpper");
      if(menuParentClass){
        //if any new menu levels come in, add here to this array
        let targetClass = ["menu-block", "subMenu-li", "nav-link-item"];
        try{
           //Using event bubbling to keep checking till the root menu class which is held in menuParentClass
            while(target!== menuParentClass){
              //checking for all items in the array defined above to find our target class
              const containsClass = targetClass.some(cls => target.classList.contains(cls));
              if(containsClass && !toShow){
                target.classList.add("hidden");
                break;
              }
              else if(containsClass && toShow){
                target.classList.remove("hidden");
                break;
              }
              else if(target.parentNode){
                //traversing one level up in the DOM tree.                 
                target = target.parentNode;
              }
              else{
                break;
              }
            }
        }
        catch(err){
          console.log(`Error -${err}- in handling menu hide function for ${menuToBeHidden} menu`);
        }
      }
      else{
        console.log("Error parent menu class name has been modified from the original 'main-nav-warpper'. Exiting hiding")
      }
      
    }
  }

  async fetchPhotoGalleryData() {
    const weblocationId = (document.getElementById("weblocationId") as HTMLInputElement)?.value ?? localStorage.getItem("franchiseWebLocationId") ??  0;
    let photos : any = localStorage.getItem(`galleryPhotos-${weblocationId}`); 
    if (!photos) {
      const conceptId = conceptIdForAllBrands();
      if(!conceptId) {
        return;
      }
      const categoryID = 0;
      const imagesApi = `${apiConfig.PhotosAPI}/${weblocationId}/${conceptId}/${categoryID}?skip=100`;

      try {
        const imageResponse = await cachedAPIWrapper(imagesApi);
        photos = imageResponse?.photos ?? [];
        localStorage.setItem(`galleryPhotos-${weblocationId}`, JSON.stringify(photos));
      } catch (error) {
        console.error("Error fetching photo gallery data:", error);
      }
    }
    else{
      photos = JSON.parse(photos);
    }
    if(photos && photos.length < 6) {
      this.hideTeamAndPhotoMenu("photo", false);
    }
    else{
      this.hideTeamAndPhotoMenu("photo", true);
    }
  }

  async getFooterMenu(dbaNameOld: any) {
    const dbaName: string | null = localStorage.getItem('dbaName') ?? dbaNameOld;
    if(dbaName) {
      const brandDataReady = await waitForBrandDataBeforeFooterConfig();
      
      if (!brandDataReady) {
        console.warn('Brands.json not loaded, making another brands.json call soon');
        const conceptCode = (document.getElementById('conceptCode') as HTMLInputElement)?.value;
        const brandData = await getBrandDetailsFromBrandJson(conceptCode);
        if (brandData) {
          console.log('Brands.json loaded successfully as a fallback on 2nd attempt');
        }
      }
      
      try {
        const urlName = await getWeblocationPathGroupWithFooterConfig();
        const request = {
          method: 'GET',
          url: urlName
        };
        
        try {
          const response: any = await apiRequest(request);
          this.insertDataIntoFooterMenu(response);
          replaceNeighborlyFooter(response);
          replaceLegalFooter(response);
        } catch (apiError) {
          console.log('Footer menu API request failed:', apiError);
        }
      } catch (err) {
        console.log('Error getting footer config URL:', err);
      }finally{
        // Modify All Apply Locally hrefs - runs after footer menu initialization
        modifyAllApplyLocalHref();
      }
    }
  
  }
      

  async insertDataIntoFooterMenu(footerHTML? : any) {
    const parser = new DOMParser();
    const newFooter = parser.parseFromString(footerHTML, 'text/html');
    const footerConfigHTML = document.querySelector('.fb-wrapper-link') as HTMLElement;
    const newFooterLinks = newFooter.querySelector('.fb-wrapper-link');
    if(newFooterLinks && footerConfigHTML) {
      footerConfigHTML.innerHTML = newFooterLinks.innerHTML
    }
    this.getLocalHeaderData();
  }

  getLocalHeaderData() {
    // service links 
    const headerLinks: any = document.querySelectorAll('.dynamic-service-menu')
    let data: any = []
    if (headerLinks.length > 0) {
    headerLinks.forEach((item: any) => {
        data.push({
            name: item?.querySelector('a span').textContent.trim(),
            link: item?.querySelector('a').href

        })
    })
    setServices(data);
   } else {
      let serviceData:any = getFooterLink();
      setServices(serviceData);
      }
  }

  async getMenu() {
    const dbaName = (document.getElementById('dbaName') as HTMLInputElement)?.value ?? localStorage.getItem('dbaName');
    const webId = (document.getElementById('weblocationId') as HTMLInputElement)?.value ?? localStorage.getItem('weblocationId');
    const locationDoingBusinessAs = localStorage.getItem('doingBusinessAs');
    const url = window.location.origin // === "localhost" ? `https://dig-www-nei-mre2-test.nblydev.com` : window.location.origin
    let dynamicUrl = `${url}/localmenu/${webId}/${dbaName}`;
    const brandData = localStorage.getItem('brandDetails') ? JSON.parse(localStorage.getItem('brandDetails') as string) : null;
    let weblocationInfo = window?.crownpeakWeblocationsByIdDetail;
    if(!weblocationInfo && brandData?.include_statename){
      await fetchCrownpeakUrlDetails();
      weblocationInfo = window?.crownpeakWeblocationsByIdDetail;
    }
    
    if(brandData?.include_statename && weblocationInfo){
      dynamicUrl = `${url}/localmenu/${webId}/${dbaName}/${weblocationInfo?.stateName}`;
    }

    const request = {
      method: 'GET',
      url: dynamicUrl,
    };
    try {
    const response:any = await apiRequest(request);
    this.insertMenuIntoDOM(response);
    await StickyHeader.init();
    const setLocalHeader = new SetLocalHeader();
    await setLocalHeader.initAsync();
    
    this.getFooterMenu(dbaName);
    if(dbaName && locationDoingBusinessAs){
      const newHTML = `
          <svg aria-hidden="true" class="icon"> <use xlink:href="/brand/_assets/images/icons/hero-icon-sprite.svg#map-pin-primary"></use></svg>
              <div>
                  <span class="local-address">${locationDoingBusinessAs}</span> Change location
              </div>
          `;
          setLocalHeader.setHamburgerSubMenuInnerDba(newHTML)
  }
    
    } catch (error) {
      console.error("Error fetching menu:", error);
    }
  }


  insertMenuIntoDOM(menuHTML?: string) {
    if (menuHTML) {
      const mainNav = document.querySelector('.main-nav');
      // Prefer the visible .brand-header list; fall back to the first .brand-cta-list for the swapped desktop state.
      const topNav = document.querySelector('.header-wrapper .brand-header .brand-cta-list') ??
                     document.querySelector('.header-wrapper .brand-cta-list');
      const parser = new DOMParser();
      const newNav = parser.parseFromString(menuHTML, 'text/html');
      const newMainNav = newNav.getElementById("bottom-menu") as HTMLElement;
      const newTopNav = newNav.getElementById("top-menu") as HTMLElement;

      if (mainNav && newMainNav) {
        mainNav.innerHTML = newMainNav.innerHTML; // Set innerHTML to an empty string if menuHTML is undefined
        // new MegaMenuHandler()
      }
      if (topNav && newTopNav) {
        topNav.innerHTML = newTopNav.innerHTML;
      }
      const localWeblocId = (document.getElementById('local_weblocationId') as HTMLInputElement)?.value;
      const franchiseWebLocationId = localStorage.getItem("franchiseWebLocationId");
      if (franchiseWebLocationId || localWeblocId) {
        this.hideOurTeamsMenu();
      }
      this.fetchPhotoGalleryData();

      const newLogoPath = newNav?.getElementById("local-header-logoimagepath") as HTMLElement;
      const newLogoAlt = newNav?.getElementById("local-header-logoimagealt") as HTMLElement;
    //Considering only localheader src and alt text from local menu and updating both header and footer with the same details
       if (newLogoPath && newLogoAlt) {
        updateHeaderLogo(
          newLogoPath.textContent?.trim(), 
          newLogoAlt.textContent?.trim()
        );
      } 

      try {
          // After inserting the menu, we can initialize the NavDropdown if the screen type is Desktop
          const screenType = reportScreenSize();
          if (screenType === ScreenType.Desktop) {
              initializeNavDropdown();
          }

          
      } catch (error) {
        console.error("Error in updating NavDropdown:", error);
        
      }

      try {
        // After local menu is inserted appending the Event Listeners for the menus
          handleMobileDesktopSubmenuClicks();
      } catch (error) {
        console.error("Error in handling mobile/desktop submenu clicks:", error);

      }
    }
  }

}

// Initialize the menu generator asynchronously
void (async () => {
  await MenuGeneratorDynamic.create();
})();
